import React, { PropsWithChildren, useMemo } from "react";

import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Timeline from "@material-ui/lab/Timeline";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineOppositeContent from "@material-ui/lab/TimelineOppositeContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import ApartmentIcon from "@material-ui/icons/Apartment";
import CategoryIcon from "@material-ui/icons/Category";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import DeleteForeverOutlinedIcon from "@material-ui/icons/DeleteForeverOutlined";
import EventIcon from "@material-ui/icons/Event";
import PersonIcon from "@material-ui/icons/Person";
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import NotesIcon from "@material-ui/icons/Notes";

import { dateFromUtcMinutes, ignoreDateDST } from "../../../utils";
import useReservationPreviewStyle from "./reservation_preview.style";
import { Reservation } from "../../../graphql/models";

export interface ReservationPreviewProps extends PropsWithChildren<any> {
  value: Reservation;
}

export function ReservationPreview(props: ReservationPreviewProps) {
  const { value } = props;

  const eventDateAndTimeString = useMemo(() => {
    // if (value) {
    //   const [firstSlot, lastSlot] = [
    //     value.slots[0],
    //     value.slots[value.slots.length - 1],
    //   ];
    //   const [fromDateInstance, toDateInstance] = [
    //     ignoreDateDST(dateFromUtcMinutes(new Date(), firstSlot.fromTime)),
    //     ignoreDateDST(dateFromUtcMinutes(new Date(), lastSlot.toTime)),
    //   ];
    //   const startDateInstance = new Date(value.startDate);

    //   return `${startDateInstance.toLocaleDateString()}, dalle ${fromDateInstance.toLocaleTimeString()} alle ${toDateInstance.toLocaleTimeString()}`;
    // }

    if (value)
      return `da: ${value.bookingTexts?.startText} a: ${value.bookingTexts?.endText}`;

    return "";
  }, [value]);

  const arrivalDateInstance = useMemo(() => {
    return value?.bookingTexts?.startText ?? "n.d.";
  }, [value]);

  const isDeleted = useMemo(() => {
    return !!value?.deletionDate;
  }, [value?.deletionDate]);

  const deletionDate = useMemo(() => {
    return new Date(value?.deletionDate as number);
  }, [value?.deletionDate]);

  const classes = useReservationPreviewStyle();

  return (
    <>
      <Paper elevation={3} style={{ width: "100%", padding: "1rem" }}>
        <List
          component="nav"
          aria-labelledby={`reservation-${value?.id}-reservation-data`}
          style={{ width: "100%" }}
          subheader={
            <>
              <ListSubheader
                component="div"
                id={`reservation-${value?.id}-reservation-data-subheader`}
              >
                Informazioni prenotazione
              </ListSubheader>
            </>
          }
        >
          {value?.didntShowUp === true && (
            <ListItem>
              <ListItemIcon>
                <ReportProblemIcon color="error" />
              </ListItemIcon>
              <Typography variant="caption">
                Il condomino non si è presentato all'appuntamento.
              </Typography>
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <ApartmentIcon />
            </ListItemIcon>
            <Typography variant="caption">{value?.building.name}</Typography>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <MeetingRoomIcon />
            </ListItemIcon>
            <Typography variant="caption">{value?.apartment.code}</Typography>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CategoryIcon />
            </ListItemIcon>
            <Typography variant="caption">{value?.service.name}</Typography>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <Typography variant="caption">{eventDateAndTimeString}</Typography>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <EmojiPeopleIcon />
            </ListItemIcon>
            <Typography variant="caption">
              {value?.esclusive && <strong>- Prenotazione Esclusiva -</strong>}
              {!value?.esclusive && <span>N. {value?.units} persone</span>}
            </Typography>
          </ListItem>
          {value?.createdBy && (
            <ListItem>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <Typography variant="caption">
                Creata da: {value?.createdBy?.name}
              </Typography>
            </ListItem>
          )}
          {value?.targetUser && (
            <ListItem>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <Typography variant="caption">
                Prenotato per: {value?.targetUser.name}
                {value?.targetUser.phone && (
                  <>
                    <br /> Tel: {value?.targetUser.phone}
                  </>
                )}
              </Typography>
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <NotesIcon />
            </ListItemIcon>
            <Typography variant="caption">
              Note: {value?.notes?.trim() ? value.notes : "-"}
            </Typography>
          </ListItem>
        </List>
      </Paper>
      <Timeline align="alternate">
        <TimelineItem>
          <TimelineOppositeContent>
            <Typography variant="body2" color="textSecondary">
              {arrivalDateInstance}
            </Typography>
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="primary" />
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Paper elevation={3} className={classes.paper}>
              <Typography variant="body2" color="textSecondary">
                Arrivo in {value?.service.name}
              </Typography>
            </Paper>
          </TimelineContent>
        </TimelineItem>
        <TimelineItem>
          <TimelineOppositeContent>
            <Typography variant="body2" color="textSecondary">
              {value?.bookingTexts?.endText ?? "n.d."}
            </Typography>
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color="primary" />
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Paper elevation={3} className={classes.paper}>
              <Typography variant="body2" color="textSecondary">
                Fine prenotazione
              </Typography>
            </Paper>
          </TimelineContent>
        </TimelineItem>
        {isDeleted && (
          <TimelineItem>
            <TimelineOppositeContent>
              <Typography variant="body2" color="textSecondary">
                {deletionDate.toLocaleDateString()}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot color="secondary" variant="outlined">
                <DeleteForeverOutlinedIcon color="secondary" />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Paper elevation={3} className={classes.paper}>
                <Typography variant="body2" component="h3">
                  <strong>{value?.deletedBy?.name}</strong> ha annullato la
                  prenotazione.
                </Typography>
              </Paper>
            </TimelineContent>
          </TimelineItem>
        )}
      </Timeline>
    </>
  );
}
